import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { isString, uniqBy } from 'lodash';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { serializeHtml } from '@udecode/plate-serializer-html';
import { createPlateEditor } from '@udecode/plate-core';
import { getBase64 } from 'utils/images';
import { generateUUID } from 'utils/general';
import parse from 'html-react-parser';

import { styledTheme } from 'globalStyles/theme';
import APFormGroup from 'components/FormFields/APFormGroup';
import Clickwrap from 'components/Clickwrap';
import InputField from 'components/FormFields/InputField';
import FormSelect from 'components/FormFields/FormSelect';
import PlateEditorField from 'components/PlateEditor/PlateEditorField';
import { plugins } from 'components/PlateEditor/contexts/editorField';
import deserializeString from 'components/PlateEditor/utils/deserializeString';
import { convertMergeFields } from 'components/PlateEditor/plugins/merge-fields/convertMergeFields';
import { Loading } from 'components/Loading';
import DeleteConfirmation from 'components/DeleteConfirmation';
import { sanitizeHtml } from 'pages/Notes/utils/html';
import Placeholder from 'assets/images/placeholder_file-lines.svg';
import { getEmailPreview } from 'api/email';
import EmailPreview from './EmailPreview';

import './email-styles.scss';
import './styles.scss';

const EmailEditor = ({
  title,
  senderEmail,
  recipients = [],
  cc = [],
  bcc = [],
  subject = '',
  message = '',
  onSubmit,
  status,
  allowCopies = false,
  allowBccSender = true,
  templates = [],
  threads = [],
  replyMessage,
  onDiscard,
  mergeFields = [],
  dataLoaded,
  allowFormatting = true,
  disableRecipient = false,
  handlePreviewToggle = () => {},
  preview,
  scrollToOnMount = false,
}) => {
  const editorRef = useRef();
  const [emailSender, setEmailSender] = useState(senderEmail[0]);
  const [primaryEmail, setPrimaryEmail] = useState(recipients);
  const [ccEmail, setCCEmail] = useState(cc);
  const [showCc, setShowCc] = useState(cc?.length ? true : null);
  const [bccEmail, setBCCEmail] = useState(bcc);
  const [showBcc, setShowBcc] = useState(bcc?.length || null);
  const [bccSender, toggleBCCSender] = useState(bcc?.length || false);
  const [subjectEmail, setSubjectEmail] = useState(subject);
  const [messageEmail, setMessageEmail] = useState(message);
  // Need the raw message to prevent a change of effect hooks firing.
  const [messageEditor, setMessageEditor] = useState(message);
  const [groupedTemplates, setGroupedTemplates] = useState([]);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const [isFormValid, setFormValid] = useState(true);
  const [error, setError] = useState('');
  const [bodyError, setBodyError] = useState(false);
  const [recipientError, setRecipientError] = useState(false);
  const [ccError, setCcError] = useState('');
  const [bccError, setBccError] = useState('');

  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(-1);
  const [emailFormData, setEmailFormData] = useState(null);
  const [attachmentsData, setAttachmentsData] = useState([]);

  const maxFilesize = 20 * 1024 * 1024; // 20MB

  const { thread_id } = replyMessage || {};
  const currentThread = thread_id ? threads.find(thread => thread.id === thread_id) : null;
  const { latest_draft_or_message } = currentThread || {};
  const { body } = latest_draft_or_message || {};

  useEffect(() => {
    // prevent resetting primaryEmail's value after the send button is clicked
    if (recipients.length && !primaryEmail.length) {
      setPrimaryEmail(recipients);
    }
  }, [recipients, primaryEmail]);

  useEffect(() => {
    const groupedData = templates.reduce((acc, item) => {
      const existingKind = acc.find(group => group.kind === item.kind);
      if (existingKind) {
        existingKind.templates.push(item);
      } else {
        acc.push({ kind: item.kind, templates: [item] });
      }
      return acc;
    }, []);

    setGroupedTemplates(groupedData);
  }, [templates]);

  useEffect(() => {
    if (scrollToOnMount && editorRef.current) {
      editorRef.current.scrollIntoView();
    }
  }, [editorRef]);

  useEffect(() => {
    removeErrorMessage();
  }, [primaryEmail, subjectEmail, messageEmail]);

  const emailValid = async (email) => {
    async function validateEmail(email_) {
      const re = /\S+@\S+\.\S+/;

      return re.test(email_);
    }

    let emailArr = email;
    if (isString(email)) {
      emailArr = getEmailArray(email);
    }

    const isValidPromises = emailArr.map(async email_ => validateEmail(email_));

    const resolvedPromises = await Promise.all(isValidPromises);

    return resolvedPromises.every(isValid => isValid);
  };

  const handleTemplateSelect = (e) => {
    const templateId = Number(e.target.value);
    const template = templates.find(item => item.id === templateId);

    if (template) {
      const {
        message: templateMsg,
        subject: templateSubject,
      } = template;
      setMessageEditor(templateMsg);
      setSelectedTemplate(templateId);

      const convertedMergeFields = convertMergeFields(templateMsg, mergeFields);

      // Preliminary check for merge field errors.
      // Set error if any merge field errors are found.
      if (convertedMergeFields.includes('merge-field-error')) {
        setBodyError(true);
      }

      // Create temp editor to serialize the input string to
      // html generated from the editor.
      const _editor = createPlateEditor({ plugins });
      const html = serializeHtml(
        _editor,
        {
          nodes: deserializeString(convertedMergeFields),
          convertNewLinesToHtmlBr: true,
          preserveClassNames: ['slate'],
        },
      );
      setMessageEmail(html);

      // If replying, do not overwrite the subject.
      if (!replyMessage || !Object.keys(replyMessage).length) {
        setSubjectEmail(templateSubject);
      }
    }
    else {
      // #TODO: set error.
    }
  };

  const getEmailArray = (str) => {
    return str
      .split(',')
      .map(email => email.trim())
      .filter(email => email !== '');
  };

  const isEmailFormValid = async () => {
    let isValid = true;
    if (!(await emailValid(primaryEmail))) {
      setRecipientError(true);
      setError(
        'It looks like the email you’ve entered is invalid. ' +
        'Please check the email addresses entered above and update as needed.',
      );
      isValid = false;
    } else {
      setRecipientError(false);
    }

    if (!subjectEmail || !messageEmail) {
      setError('Please complete all required fields above.');
      isValid = false;
    }

    if (!messageEmail || messageEmail.includes('merge-field-error')) {
      setBodyError(true);
      setError('Please complete all required fields above.');
      isValid = false;
    }

    if (showCc && !ccEmail.length || showCc && !(await emailValid(ccEmail))) {
      setCcError('One or more email addresses have a wrong format.');
      setError('Please complete all required fields above.');
      isValid = false;
    } else {
      setCcError('');
    }

    if (showBcc && !bccEmail.length || showBcc && !(await emailValid(bccEmail))) {
      setBccError('One or more email addresses have a wrong format.');
      setError('Please complete all required fields above.');
      isValid = false;
    } else {
      setBccError('');
    }

    if (selectedTemplate !== -1) {
      const selectedTemplateData = templates.find(item => item.id === selectedTemplate);
      if (selectedTemplateData) {
        const deserializedMessage = deserializeString(messageEmail);
        // Is this consistent across all templates?
        const plainDeserializedMessage = deserializedMessage[0].children[0].children.map(c => c.text).join(' ');
        const convertedMergeFields = convertMergeFields(plainDeserializedMessage, mergeFields);
        if (convertedMergeFields.includes('merge-field-error')) {
          setBodyError(true);
          setError(
            'Sorry, we couldn’t reconcile all of the merge fields in your email with the data specified. ' +
            'Please replace any red merge fields with the actual data and try again.',
          );
          isValid = false;
        } else {
          setBodyError(false);
        }
      }
    }

    return isValid;
  };

  const removeErrorMessage = () => {
    if (primaryEmail && subjectEmail && messageEmail) {
      setError('');
      setBodyError(false);
    }
  };

  const onEmailSend = async () => {
    setFormSubmitted(true);
    if (await isEmailFormValid()) {
      setError('');
      setFormValid(true);

      const ccEmailArr = isString(ccEmail) ? getEmailArray(ccEmail) : ccEmail.map(e => e?.email || e);
      const bccEmailArr = isString(bccEmail) ? getEmailArray(bccEmail) : bccEmail.map(e => e?.email || e);

      const emailData = {
        sender: emailSender,
        recipients: isString(primaryEmail) ? getEmailArray(primaryEmail) : primaryEmail,
        cc: (showCc && ccEmailArr.length) ? ccEmailArr : null,
        bcc: (showBcc && bccEmailArr.length) ? bccEmailArr : null,
        subject: subjectEmail,
        message: messageEmail,
        attachments: attachmentsData,
      };

      if (replyMessage?.id) {
        emailData.replyId = replyMessage.id;
      }

      try {
        await onSubmit(emailData);
      } catch(err) {
        // eslint-disable-next-line
        console.log(err);
      }
    } else {
      setFormValid(false);
    }
  };

  const onPreviewClick = async () => {
    let htmlMessage = null;
    try {
      const response = await getEmailPreview('lead-email', messageEmail, true);
      if (response.ok) {
        htmlMessage = response.data;
      } else {
        // eslint-disable-next-line
        console.error('Failed to get email preview :', response);
      }
    } catch (err) {
      // eslint-disable-next-line
      console.error('Failed to get email preview :', err);
    }

    setEmailFormData({
      recipients: primaryEmail,
      cc: ccEmail,
      bcc: bccEmail,
      bccSender,
      subject: subjectEmail,
      message: messageEmail,
      htmlMessage,
      attachments: attachmentsData,
    });

    handlePreviewToggle(true);
  };

  const handleDiscardClick = () => {
    setShowDiscardModal(true);
  };

  const convertToMb = (bytes) => {
    if (!+bytes) return '0 Bytes';

    const bytesInMb = 1024 * 1024;
    return bytes / bytesInMb;
  };

  const handleFileChange = (e) => {
    if (e.target.files.length === 0) return;

    const filesFilteredBySize = [...e.target.files].filter(({ size: fileSize }) => {
      return Math.floor(convertToMb(fileSize)) <= convertToMb(maxFilesize);
    });

    if (filesFilteredBySize.length !== e.target.files.length) {
      setError(`Attachments cannot be larger than ${convertToMb(maxFilesize).toFixed(2)} Mb`);
      setFormValid(false);
    } else {
      setError('');
      setFormValid(true);
    }

    const promises = filesFilteredBySize.map(async (file) => {
      const { name, size: fileSize, type: fileType } = file;

      return {
        content: await getBase64(file),
        content_type: fileType,
        filename: name,
        id: generateUUID(),
        is_inline: false,
        size: fileSize,
      };
    });

    Promise.all(promises)
      .then((response) => {
        const uniqueFiles = uniqBy([...attachmentsData, ...response], 'filename');
        setAttachmentsData(uniqueFiles);
      })
      .catch((err) => {
        // eslint-disable-next-line
        console.error(err);
      });
  };

  const removeFile = (id) => {
    const filteredFiles = attachmentsData.filter(file => file.id !== id);
    setAttachmentsData(filteredFiles);
  };

  if (!dataLoaded) {
    // TODO: Revisit for error handling/logging.
    // eslint-disable-next-line
    console.log('Data not loaded');
    return null;
  }

  if (preview) {
    return (
      <EmailPreview
        formData={emailFormData}
        onBack={() => {
          handlePreviewToggle(false);
        }}
      />
    );
  }

  return (
    <>
      <section className="email-editor__content">
        {status !== '' ? <Loading /> : null}
        {
          title && (
            <h2 className="email-editor__title">
              { title }
            </h2>
          )
        }
        {body && (
          <div className="email-editor__previous-message">
            { parse(sanitizeHtml(body, false, [], [])) }
          </div>
        )}
        <div
          ref={editorRef}
          className="email-editor__form"
        >
          {senderEmail.length > 1 ? (
            <APFormGroup className="email-editor__sender-group">
              <FormSelect
                name="From"
                onChange={(e) => setEmailSender(e.target.value)}
                value={emailSender}
              >
                {senderEmail.map((email, index) => {
                  return (
                    <option
                      key={`sender-option-${index}`}
                      value={email}
                    >
                      {email}
                    </option>
                  );
                })}
              </FormSelect>
            </APFormGroup>
          ) : null}
          <APFormGroup
            className={clsx(
              'email-editor__address-group',
              allowCopies &&
              'email-editor__address-group--allow-copies',
            )}
          >
            <APFormGroup
              direction="vertical"
              className="email-editor__recipient-field"
            >
              <InputField
                name="primaryAddress"
                label="Recipients"
                sublabel="Separate multiple email addresses with commas."
                value={primaryEmail}
                onChange={(event) => setPrimaryEmail(event.target.value)}
                showError={isFormSubmitted && recipientError}
                required
                disabled={disableRecipient}
              />
            </APFormGroup>
            {allowCopies && (
              <div className="email-editor__copy-buttons">
                {!showCc && (
                  <span
                    className="email-editor__add-copy"
                    onClick={() => {
                      setShowCc(true);
                    }}
                  >
                    CC +
                  </span>
                )}
                {!showBcc && (
                  <span
                    className="email-editor__add-copy"
                    onClick={() => {
                      setShowBcc(true);
                    }}
                  >
                    BCC +
                  </span>
                )}
              </div>
            )}
          </APFormGroup>
          {showCc && (
            <APFormGroup className="email-editor__bcc-input">
              <InputField
                name="ccAddress"
                showLabel={false}
                value={`CC: ${isString(ccEmail) ? ccEmail : ccEmail.map(e => e.email).join(', ')}`}
                onChange={(event) => setCCEmail(event.target.value.substring(4))}
                showError={isFormSubmitted && ccError !== ''}
              />
              <span
                className="email-editor__bcc-remove"
                onClick={() => {
                  setShowCc(false);
                  setCCEmail('');
                }}
              >
                <HighlightOffIcon style={{ fontSize: '23px', color: styledTheme.errorColor }} />
              </span>
            </APFormGroup>
          )}
          {showBcc && (
            <APFormGroup className="email-editor__bcc-input">
              <InputField
                name="bccAddress"
                showLabel={false}
                value={`BCC: ${isString(bccEmail) ? bccEmail : bccEmail.map(e => e.email).join(', ')}`}
                onChange={(event) => setBCCEmail(event.target.value.substring(5))}
                showError={isFormSubmitted && bccError !== ''}
              />
              <span
                className="email-editor__bcc-remove"
                onClick={() => {
                  setShowBcc(false);
                  setBCCEmail('');
                }}
              >
                <HighlightOffIcon style={{ fontSize: '23px', color: styledTheme.errorColor }} />
              </span>
            </APFormGroup>
          )}
          {allowCopies && allowBccSender && (
            <APFormGroup className="email-editor__sender-group">
              <div className="email-editor__bcc-sender-checkbox">
                <Clickwrap
                  name="bcc-sender"
                  checked={bccSender}
                  onChange={() => toggleBCCSender(!bccSender)}
                  tabIndex="-1"
                />
                <label htmlFor="bcc-sender">
                  BCC sender
                </label>
              </div>
            </APFormGroup>
          )}
          {groupedTemplates.length ? (
            <APFormGroup className="email-editor__template-group">
              <FormSelect
                name="Template"
                onChange={handleTemplateSelect}
                value={selectedTemplate}
              >
                {groupedTemplates.map((group, index) => (
                  <optgroup
                    key={`optgroup-${index}`}
                    label={`${group.kind.replaceAll('-', ' ')} Email Templates`}
                  >
                    {group.templates.map(template => (
                      <option
                        key={`option-${template.id}`}
                        value={template.id}
                      >
                        {template.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </FormSelect>
            </APFormGroup>
          ) : null}
          <APFormGroup className="email-editor__subject-group">
            <InputField
              name="subject"
              label="Subject"
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
              disabled={replyMessage?.id}
              showError={isFormSubmitted && !subjectEmail}
              required
            />
          </APFormGroup>
          <APFormGroup className="email-editor__message-group">
            <PlateEditorField
              key={`message-field-${selectedTemplate}`}
              onChange={(value) => setMessageEmail(value)}
              value={messageEditor}
              setEditorValue={(value) => setMessageEditor(value)}
              onChangeMode="html"
              required
              labelHidden={false}
              fieldLabel="Message"
              error={bodyError}
              setError={setBodyError}
              mergeFields={mergeFields}
              editableProps={{ className: 'editor-field editor-field--email' }}
              allowFormatting={allowFormatting}
              isFormSubmitted={isFormSubmitted}
            />
          </APFormGroup>
          <APFormGroup>
            <label
              htmlFor="file-input"
              className="email-editor__file-label"
            >
              <input
                type="file"
                id="file-input"
                name="attachment"
                className="email-editor__file-input"
                onChange={handleFileChange}
                multiple
              />
            </label>
          </APFormGroup>

          {attachmentsData.length ? (
            <div className="email-editor__attachments">
              <div className="email-editor__subtitle">Attachment(s)</div>
              {attachmentsData.map(file => {
                const [fileType] = file.content_type.split('/');

                return (
                  <div
                    className="email-editor__attachment"
                    key={file.id}
                  >
                    <div className={clsx('email-editor__attachment-image', fileType !== 'image' && 'type-doc')}>
                      <img src={fileType === 'image' ? file.content : Placeholder}/>
                    </div>
                    <div className="email-editor__attachment-name">
                      {file.filename}
                    </div>
                    <div className="email-editor__attachment-remove">
                      <HighlightOffIcon
                        style={{ fontSize: '22px', color: styledTheme.errorColor }}
                        onClick={() => removeFile(file.id)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          <APFormGroup className="email-editor__preview-group">
            <button
              className={clsx('button--unstyled', 'email-editor__preview')}
              onClick={onPreviewClick}
            >
              Preview
            </button>
          </APFormGroup>
          {!isFormValid && (
            <div className="validation__error">
              {error}
            </div>
          )}
          <div className="email-editor__footer">
            <button
              type="button"
              className="button button--cancel"
              onClick={handleDiscardClick}
            >
              Discard
            </button>
            <button
              type="button"
              className="button"
              onClick={onEmailSend}
              disabled={status !== ''}
            >
              Send
            </button>
          </div>
        </div>
      </section>
      <DeleteConfirmation
        show={showDiscardModal}
        handleClose={() => {
          setShowDiscardModal(false);
        }}
        handleConfirm={onDiscard}
        deleteText="Discard"
        size="xs"
      >
        <p>Discard draft? This will permanently delete the email and cannot be undone.</p>
      </DeleteConfirmation>
    </>
  );
};

EmailEditor.propTypes = {
  title: PropTypes.string,
  recipients: PropTypes.array,
  cc: PropTypes.array,
  bcc: PropTypes.array,
  subject: PropTypes.string,
  message: PropTypes.string,
  onSubmit: PropTypes.func,
  status: PropTypes.string,
  allowCopies: PropTypes.bool,
  templates: PropTypes.array,
  threads: PropTypes.array,
};

export default EmailEditor;
